"use client"

import type React from "react"

import { useState, useRef, useEffect } from 'react'
import { X, Search, Clock, CheckCircle, AlertCircle, MapPin } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useSimplePet } from "@/context/SimplePetContext"
import { petStore } from "@/lib/petStore"

interface Pet {
  id: number;
  name: string;
  ownerFirstName: string;
  ownerLastName: string;
  status: "care" | "completed" | "awaiting" | "scheduled";
  earnings: number;
  consentSigned: boolean;
  billing?: string;
  clinicId: string;
  createdAt: string;
  updatedAt: string;
}

interface TrackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPetFound?: (pet: Pet) => void;
}

export default function TrackModal({ isOpen, onClose, onPetFound }: TrackModalProps) {
  const { setCurrentPet } = useSimplePet()
  const [formData, setFormData] = useState({
    petName: "",
    ownerLastName: "",
  })
  const [pet, setPet] = useState<Pet | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [searched, setSearched] = useState(false)

  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setPet(null)
    setSearched(true)

    try {
      console.log('Searching for pet:', formData);
      const response = await fetch(`/api/pets/track?name=${encodeURIComponent(formData.petName)}&ownerLastName=${encodeURIComponent(formData.ownerLastName)}`)
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error("Failed to search for pet")
      }

      const data = await response.json()
      console.log('Response data:', data);
      
      if (data.pet) {
        setPet(data.pet)
        // Store the pet in the global store for persistence across pages
        petStore.setCurrentPet(data.pet)
        console.log('Pet set in store from track modal:', data.pet.name)
        
        // Call the onPetFound callback if provided
        if (onPetFound) {
          onPetFound(data.pet)
        }
      } else {
        setError("No pet found with that name and owner surname. Please check the spelling and try again.")
      }
    } catch (error) {
      setError("Error searching for pet. Please try again.")
      console.error("Error:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({ petName: "", ownerLastName: "" })
    setPet(null)
    setError("")
    setSearched(false)
  }

  const handleClose = () => {
    onClose()
    // Don't clear the pet from context when closing - keep it for purchases
  }

  const handleCancel = () => {
    // Clear the pet from context when canceling
    setCurrentPet(null)
    onClose()
  }

  const handleOK = () => {
    // Keep the pet in context and close the modal
    onClose()
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'care':
        return <Clock className="w-4 h-4 text-blue-600" />
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'awaiting':
        return <AlertCircle className="w-4 h-4 text-yellow-600" />
      case 'scheduled':
        return <Clock className="w-4 h-4 text-purple-600" />
      default:
        return <AlertCircle className="w-4 h-4 text-gray-600" />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'care':
        return 'In Care'
      case 'completed':
        return 'Completed'
      case 'awaiting':
        return 'Awaiting'
      case 'scheduled':
        return 'Scheduled'
      default:
        return 'Unknown'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'care':
        return 'bg-blue-100 text-blue-800'
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'awaiting':
        return 'bg-yellow-100 text-yellow-800'
      case 'scheduled':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99] p-4">
      <div 
        ref={modalRef}
        className="bg-white rounded-[20px] max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-[24px] font-semibold text-[#002e65]">
            Track your pet
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {!searched ? (
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-[#002e65] text-[16px] mb-2">
                  Enter your pet's name and owner's surname to track their status
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="petName" className="text-[#002e65] font-medium">
                    Pet Name
                  </Label>
                  <Input
                    id="petName"
                    name="petName"
                    type="text"
                    value={formData.petName}
                    onChange={handleChange}
                    placeholder="Enter your pet's name"
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="ownerLastName" className="text-[#002e65] font-medium">
                    Owner Surname
                  </Label>
                  <Input
                    id="ownerLastName"
                    name="ownerLastName"
                    type="text"
                    value={formData.ownerLastName}
                    onChange={handleChange}
                    placeholder="Enter owner's surname"
                    className="mt-1"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#004da9] hover:bg-[#003d87] text-white rounded-[8px] py-3"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Searching...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Search className="w-4 h-4 mr-2" />
                      Track status
                    </div>
                  )}
                </Button>
              </form>
            </div>
          ) : loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004da9] mx-auto mb-4"></div>
              <p className="text-[#002e65] text-[16px]">Searching for your pet...</p>
            </div>
          ) : pet ? (
            <div className="space-y-6">
              {/* Pet Found */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <p className="text-green-800 font-medium">Pet found!</p>
                </div>
              </div>

              {/* Pet Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-[#002e65] text-[24px] font-semibold mb-4">{pet.name}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-gray-600">Owner</p>
                    <p className="text-[#002e65] font-medium">{pet.ownerFirstName} {pet.ownerLastName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Created</p>
                    <p className="text-[#002e65] font-medium">{formatDate(pet.createdAt)}</p>
                  </div>
                </div>

                {/* Status */}
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-2">Status</p>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(pet.status)}`}>
                    {getStatusIcon(pet.status)}
                    <span className="ml-2">{getStatusText(pet.status)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    onClick={handleOK}
                    className="flex-1 bg-[#004da9] hover:bg-[#003d87] text-white rounded-[8px]"
                  >
                    OK
                  </Button>
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    className="flex-1 border-gray-300 text-gray-700 rounded-[8px]"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* No Pet Found */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                  <p className="text-red-800 font-medium">Pet not found</p>
                </div>
                <p className="text-red-700 text-sm mt-2">{error}</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={handleReset}
                  className="flex-1 bg-[#004da9] hover:bg-[#003d87] text-white rounded-[8px]"
                >
                  Try Again
                </Button>
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="flex-1 border-gray-300 text-gray-700 rounded-[8px]"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

